import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { NumberSequenceFormValues } from "./number-sequence-schema";
import type { SEQUENCE_PLACEHOLDERS } from "./number-sequence-utils";
import type { FieldErrors, UseFormRegister } from "react-hook-form";

interface NumberSequenceCodeFieldsProps {
  register: UseFormRegister<NumberSequenceFormValues>;
  errors: FieldErrors<NumberSequenceFormValues>;
  placeholders: typeof SEQUENCE_PLACEHOLDERS;
  insertPlaceholder: (field: "prefix" | "suffix", tag: string) => void;
}

export function NumberSequenceCodeFields({
  register,
  errors,
  placeholders,
  insertPlaceholder,
}: NumberSequenceCodeFieldsProps) {
  return (
    <>
      {/* Prefix Field with Placeholders */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="prefixInput" className="text-xs font-medium">
            Format Prefix (Awalan)
          </Label>
          <span className="text-[11px] text-muted-foreground">
            Dilarang diawali tanda slash (/)
          </span>
        </div>
        <Input
          id="prefixInput"
          {...register("prefix")}
          className="font-mono text-sm tracking-wide"
          placeholder="SALE/%(Year)s%(Month)s/"
        />
        {errors.prefix && (
          <p className="text-xs text-destructive">{errors.prefix.message}</p>
        )}

        {/* Placeholder Pills for Prefix */}
        <div className="pt-1">
          <span className="text-[11px] text-muted-foreground block mb-1.5">
            Klik tag untuk menyisipkan ke Prefix:
          </span>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map((item) => (
              <Button
                key={item.tag}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => insertPlaceholder("prefix", item.tag)}
                className="h-6 text-[11px] font-mono px-2 py-0 border-dashed hover:border-primary hover:text-primary"
                title={`${item.description} (Contoh: ${item.sample})`}
              >
                + {item.tag}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Suffix Field with Placeholders */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="suffixInput" className="text-xs font-medium">
            Format Suffix (Akhiran - Opsional)
          </Label>
          <span className="text-[11px] text-muted-foreground">
            Kosongkan jika tidak diperlukan akhiran
          </span>
        </div>
        <Input
          id="suffixInput"
          {...register("suffix")}
          className="font-mono text-sm tracking-wide"
          placeholder="/%(ExtBranchId)s"
        />
        {errors.suffix && (
          <p className="text-xs text-destructive">{errors.suffix.message}</p>
        )}

        {/* Placeholder Pills for Suffix */}
        <div className="pt-1">
          <span className="text-[11px] text-muted-foreground block mb-1.5">
            Klik tag untuk menyisipkan ke Suffix:
          </span>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map((item) => (
              <Button
                key={item.tag}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => insertPlaceholder("suffix", item.tag)}
                className="h-6 text-[11px] font-mono px-2 py-0 border-dashed hover:border-primary hover:text-primary"
                title={`${item.description} (Contoh: ${item.sample})`}
              >
                + {item.tag}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
